import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchOrderAgain,
  selectOrderAgainError,
  selectOrderAgainHasMore,
  selectOrderAgainItems,
  selectOrderAgainOffset,
  selectOrderAgainStatus,
  selectShouldFetchOrderAgain,
  ORDER_AGAIN_PAGE_SIZE,
} from "store/slices/orderAgainSlice";

import { formatCurrency } from "config/config";

import { ErrorMessage, Loading, ProductImage } from "../common";
import { stockState } from "../components/product/productPresentation";
import { STOCK } from "../design-system/components/constants";
import { addToCart } from "../redux/slices/cartSlice";

import { Button, Icon, QuantityStepper } from "@/design-system";

// The reference formats these as "12 Mar 2026" / "3 times".
const dateFmt = new Intl.DateTimeFormat("en-CA", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const formatLastOrdered = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : dateFmt.format(d);
};

const formatTimes = (count) => {
  const n = Number(count) || 0;
  if (n <= 0) return "";
  return n === 1 ? "Ordered once" : `Ordered ${n} times`;
};

const formatNextDelivery = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : dateFmt.format(d);
};

// Same `stockState`/`LOW_STOCK_THRESHOLD` the catalog grid card, list row and
// quick-look use, so a SKU's "Low stock" here matches its own listing elsewhere.
const stockFor = (item) => {
  if (item.isAvailable === false) return null;
  return STOCK[stockState(item)];
};

function OrderAgainRow({ item, onAdd }) {
  const [qty, setQty] = useState(
    Math.max(1, Number(item.requestedQuantity) || 1)
  );
  const navigate = useNavigate();

  const unavailable = item.isAvailable === false;
  const stock = stockFor(item);
  const onStanding = Boolean(item.nextDeliveryDate);
  const openPdp = () => navigate(`/product/${item.itemId}`);

  return (
    <div className="ro-row items-center gap-x-[var(--space-5)] bg-card border border-line-subtle shadow-sm rounded-lg py-[14px] px-[var(--space-5)] [transition:box-shadow_var(--duration-fast),_border-color_var(--duration-fast)] hover:shadow-sm hover:border-orange-200">
      <button
        type="button"
        onClick={openPdp}
        aria-label={item.displayName || item.sku}
        className="w-[64px] h-[64px] rounded-md bg-neutral-50 border border-line-subtle overflow-hidden inline-flex items-center justify-center cursor-pointer p-0"
      >
        <ProductImage
          src={item.imageUrl}
          alt={item.displayName || item.sku}
          className="w-full h-full object-cover"
        />
      </button>

      <div
        className="min-w-0 cursor-pointer flex flex-col gap-[2px]"
        onClick={openPdp}
      >
        {item.brand && (
          <div className="text-2xs font-bold tracking-[var(--tracking-wider)] uppercase text-[var(--text-accent)]">
            {item.brand}
          </div>
        )}
        <div className="font-semibold text-base leading-[1.35] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden [text-wrap:pretty]">
          {item.displayName || item.sku}
        </div>
        {item.sku && <div className="text-xs text-ink-3">{item.sku}</div>}
        {stock && (
          <span
            className="ro-stock-sm items-center gap-[6px] text-xs font-semibold mt-[1px]"
            style={{ color: stock.color }}
          >
            <Icon name="circle" size={9} />
            {stock.label}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-[1px] min-w-0">
        <span className="inline-flex items-center gap-[5px] text-2xs font-bold uppercase tracking-[var(--tracking-wide)] text-ink-3">
          <Icon name="history" size={14} />
          Last ordered
        </span>
        <span className="text-sm font-semibold text-ink whitespace-nowrap">
          {formatLastOrdered(item.lastOrderedDate)}
        </span>
        <span className="text-xs text-ink-3 whitespace-nowrap">
          {formatTimes(item.orderCount)}
        </span>
      </div>

      <div className="row-rating flex flex-col gap-[5px] min-w-0">
        {stock && !unavailable && (
          <span
            className="inline-flex items-center gap-[6px] text-xs font-semibold"
            style={{ color: stock.color }}
          >
            <Icon name="circle" size={9} />
            {stock.label}
          </span>
        )}
        {unavailable && (
          <span className="inline-flex items-center gap-[6px] text-xs font-semibold text-ink-3">
            <Icon name="do_not_disturb_on" size={14} />
            No longer available
          </span>
        )}
      </div>

      <div className="flex items-baseline justify-end gap-[5px] whitespace-nowrap">
        <span className="font-bold text-md">
          {item.price === null || item.price === undefined
            ? "—"
            : formatCurrency(item.price)}
        </span>
        {item.sale_unit && (
          <span className="text-2xs text-ink-3">/ {item.sale_unit}</span>
        )}
      </div>

      <div className="flex justify-center">
        {unavailable ? (
          <span className="text-sm text-ink-3">&mdash;</span>
        ) : (
          <QuantityStepper value={qty} onChange={setQty} />
        )}
      </div>

      <div className="flex justify-end">
        <Button
          size="sm"
          variant={onStanding ? "outline" : "primary"}
          className="!h-[39px]"
          startIcon={onStanding ? undefined : "add_shopping_cart"}
          fullWidth
          disabled={unavailable}
          onClick={() => onAdd(item, qty)}
        >
          {onStanding ? "Order now" : "Add"}
        </Button>
      </div>

      {onStanding && (
        <div className="col-[2_/_-1] flex items-center justify-between gap-[14px] flex-wrap mt-[var(--space-3)] pt-[11px] border-t border-line-subtle">
          <span className="inline-flex items-center gap-[6px] bg-soft border border-blue-100 text-blue-800 rounded-pill py-[5px] px-[var(--space-3)] text-xs font-semibold whitespace-nowrap">
            <Icon name="autorenew" size={15} fill className="text-blue-600" />
            Auto-delivery · next {formatNextDelivery(item.nextDeliveryDate)}
          </span>
          <span className="text-xs text-ink-3">
            Ordering now won&rsquo;t change your schedule
          </span>
        </div>
      )}
    </div>
  );
}

export default function OrderAgain() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const userInfo = useSelector((state) => state.user.info);
  const items = useSelector(selectOrderAgainItems);
  const status = useSelector(selectOrderAgainStatus);
  const error = useSelector(selectOrderAgainError);
  const hasMore = useSelector(selectOrderAgainHasMore);
  const offset = useSelector(selectOrderAgainOffset);
  const shouldFetch = useSelector(selectShouldFetchOrderAgain);

  useEffect(() => {
    if (shouldFetch && userInfo?.id) {
      dispatch(
        fetchOrderAgain({ userId: userInfo.id, getAccessTokenSilently })
      );
    }
  }, [shouldFetch, userInfo?.id, getAccessTokenSilently, dispatch]);

  const loadMore = useCallback(() => {
    if (!userInfo?.id) return;
    dispatch(
      fetchOrderAgain({
        userId: userInfo.id,
        getAccessTokenSilently,
        offset: offset + ORDER_AGAIN_PAGE_SIZE,
      })
    );
  }, [dispatch, userInfo?.id, getAccessTokenSilently, offset]);

  const handleAdd = useCallback(
    (item, qty) => {
      dispatch(
        addToCart({
          id: item.itemId,
          itemid: item.displayName,
          mpn: item.sku,
          file_url: item.imageUrl,
          price: item.price,
          pricing: item.pricing,
          totalquantityonhand: item.totalquantityonhand,
          sale_unit: item.sale_unit,
          quantity: qty,
          source: "order-again",
        })
      );
    },
    [dispatch]
  );

  const countStr = useMemo(() => {
    if (items.length === 0) return "";
    const suffix = hasMore ? "+" : "";
    return `${items.length}${suffix} ${items.length === 1 ? "item" : "items"}`;
  }, [items.length, hasMore]);

  const isFirstLoad = status === "loading" && items.length === 0;

  return (
    <div className="max-w-container my-0 mx-auto pt-[var(--space-7)] pb-[var(--space-20)] px-[var(--gutter)]">
      <div className="flex items-center gap-[var(--space-2)] text-sm text-ink-3 mb-[14px]">
        <span
          className="cursor-pointer text-ink-link"
          onClick={() => navigate("/")}
        >
          Home
        </span>
        <Icon name="chevron_right" size={16} />
        <span>Order again</span>
      </div>

      <div className="flex items-end justify-between gap-[var(--space-4)] flex-wrap">
        <span className="shrink-0 text-sm text-ink-3">{countStr}</span>
      </div>

      {error && (
        <div className="mt-[var(--space-6)]">
          <ErrorMessage message={error} />
        </div>
      )}

      {isFirstLoad ? (
        <div className="mt-[var(--space-6)]">
          <Loading text="Loading your previous orders..." />
        </div>
      ) : items.length === 0 && status === "succeeded" ? (
        <div className="text-center bg-card border border-line-subtle shadow-sm rounded-xl py-[var(--space-14)] px-[var(--space-6)] mt-[var(--space-6)]">
          <span className="w-[72px] h-[72px] rounded-full bg-soft text-blue-600 inline-flex items-center justify-center mb-[18px]">
            <Icon name="history" size={36} />
          </span>
          <h2 className="font-semibold text-2xl mb-[var(--space-2)]">
            Nothing to reorder yet
          </h2>
          <p className="text-ink-2 text-md max-w-[46ch] mt-0 mb-[22px] mx-auto">
            Once you&rsquo;ve placed an order, every product from it shows up
            here so you can restock in a tap.
          </p>
          <Button
            size="lg"
            startIcon="storefront"
            onClick={() => navigate("/products")}
          >
            Browse the catalogue
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-[10px] mt-[var(--space-6)]">
            {items.map((item) => (
              <OrderAgainRow key={item.itemId} item={item} onAdd={handleAdd} />
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center mt-[var(--space-6)]">
              <Button
                variant="outline"
                loading={status === "loading"}
                onClick={loadMore}
              >
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
